"use client";
import { useState, useRef } from "react";
import { useRouter } from "next/navigation";
import Carousel from "@/src/components/Carousel/Carousel";
import AudienceModal from "@/src/components/AudienceModal/AudienceModal";
import useModal from "@/src/hooks/useModal";
import { HomeSectionProps } from "@/src/types/home-section-props";
import { useFindSection } from "@/src/hooks/useFindSection";
import { SliderSection } from "@/src/api/fetchHomeSections/types";
import useUniqueId from "@/src/hooks/useUniqueId";
import { Swiper as SwiperInstance } from "swiper/types";
import AnimatedSliderButton from "@/src/components/AnimatedSliderButton";
import { navItems } from "@/src/constants/nav-items";
import useTextColorBasedOnBackground from "@/src/hooks/useTextColorBasedOnBackground";
import { useParseLinks } from "@/src/hooks/useParseLinks";
import { NavRoutes } from "@/src/enums/header-items";
import { getRouteFromCategory } from "@/src/utils";

const Slider: React.FC<HomeSectionProps> = ({ data }) => {
  const [hoveredColor, setHoveredColor] = useState<string>("white");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const swiperRefDesktop = useRef<SwiperInstance | null>(null);
  const swiperRefMobile = useRef<SwiperInstance | null>(null);
  const { isOpen, openPopup, closePopup } = useModal();
  const uniqueKey = useUniqueId();
  const { parseLinks } = useParseLinks();
  const router = useRouter();

  const textColorClass = useTextColorBasedOnBackground(hoveredColor);

  const section = useFindSection(data || [], "slider_with_buttons") as SliderSection | undefined;
  if (!data || !section) {
    console.error("Error: `data` is undefined or section not found.");
    return null;
  }

  const textBlock = section.text_block?.[0];
  const buttonColors = section.gallery_buttons?.[0] || {};

  const normalizeKey = (str: string) => {
    let normalized = str.toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
    if (normalized === "trade_shows") normalized = "tradeshows";
    return normalized;
  };

  const handleNavItemClick = (category: string) => {
    if (category === NavRoutes.TRADESHOWS) {
      setSelectedCategory(category);
      openPopup();
    } else {
      const route = getRouteFromCategory(category);
      router.push(route);
    }
  };

  const handleButtonHover = (index: number, color: string) => {
    swiperRefDesktop.current?.slideTo(index);
    swiperRefMobile.current?.slideTo(index);
    setHoveredColor(color || "white");
  };

  const combinedNavItems = navItems.map((item) => {
    const normalizedKey = normalizeKey(item.path);
    return {
      ...item,
      color: buttonColors[normalizedKey] ?? "#A9AEAA",
    };
  });

  return (
    <div className="bg-[#E2E3E5] h-fit py-12 flex flex-col justify-center items-center">
      <div className="relative w-full px-4 flex justify-center items-center">
        <div className="hidden lg:flex w-[70%] max-w-[1200px] ml-auto h-[550px] items-stretch overflow-hidden z-10">
          {textBlock && (
            <div
              className={`p-6 shadow-md w-[30%] flex-shrink-0 transition-colors duration-300 ease-in-out ${textColorClass}`}
              style={{ backgroundColor: hoveredColor }}
            >
              <h3 className="text-xl font-bold">{textBlock.header_text}</h3>
              <p className="whitespace-pre-line">{parseLinks(textBlock.text_area)}</p>
            </div>
          )}
          <div className="relative w-full lg:w-[70%] h-[550px]">
            <Carousel
              images={section.slider_files || []}
              uniqueKey={`${uniqueKey}-desktop`}
              swiperRef={swiperRefDesktop}
            />
          </div>
        </div>

        <div className="lg:hidden w-full h-[550px] relative">
          <div className="absolute top-0 left-0 w-full h-full z-10">
            <Carousel
              images={section.slider_files || []}
              uniqueKey={`${uniqueKey}-mobile`}
              swiperRef={swiperRefMobile}
            />
          </div>
        </div>

        <div className="absolute left-0 lg:w-[30%] w-[70%] max-w-[689px] h-[550px] overflow-hidden z-20 gap-8 flex flex-col">
          {combinedNavItems.map((item, index) => (
            <AnimatedSliderButton
              key={item.path}
              item={item.path}
              index={index}
              onMouseEnter={() => handleButtonHover(index, item.color)}
              onClick={() => handleNavItemClick(item.path)}
              color={item.color}
              totalItems={combinedNavItems.length}
            />
          ))}
        </div>
      </div>

      {textBlock && (
        <div
          className={`p-6 max-w-md mx-auto mt-4 text-center block lg:hidden transition-colors duration-300 ease-in-out ${textColorClass}`}
          style={{ backgroundColor: hoveredColor }}
        >
          <h3 className="text-xl font-bold">{textBlock.header_text}</h3>
          <p className="whitespace-pre-line text-left">{parseLinks(textBlock.text_area)}</p>
        </div>
      )}

      <AudienceModal
        isOpen={isOpen}
        onClose={closePopup}
        selectedCategory={selectedCategory}
      />
    </div>
  );
};

export default Slider;
